import {
  Button,
  type buttonVariants,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { cn } from '@folio/lib';
import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';

export interface DeactivatableButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'loading'>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: ReactNode;
  leftIcon?: ReactNode;
  isLoading?: boolean;
  rules: {
    isValid?: boolean;
    message: string;
  }[];
  children?: React.ReactNode;
  info?: string;
  tooltipLabel?: string;
}

export function DeactivatableButton({
  rules,
  children,
  isLoading,
  info,
  tooltipLabel,
  ...props
}: DeactivatableButtonProps) {
  const invalidRules = rules.filter(rule => !rule.isValid);
  const isDisabled = invalidRules.length > 0 || props.disabled;

  const showTooltip = isDisabled || !!tooltipLabel;

  return (
    <Tooltip>
      <div>
        <TooltipTrigger asChild>
          {/*
            @debt Is is a bad practice to have nested buttons, but it is unfortunately required for now:
            We want to display the tooltip when the button is disabled and a disabled button blocks events like hover
            Thus we need the tooltip a level above the button
            */}
          <div className={cn(isDisabled && 'cursor-not-allowed')}>
            <Button loading={isLoading} aria-label={tooltipLabel} {...props} disabled={isDisabled}>
              {children}
            </Button>
          </div>
        </TooltipTrigger>
        {showTooltip && (
          <TooltipContent className="text-muted-foreground">
            {tooltipLabel && <div>{tooltipLabel}</div>}
            {isDisabled && (
              <ul className="list-disc pl-4">
                {invalidRules.map(({ message }) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </TooltipContent>
        )}
        {info && <div className="text-sm font-bold text-blue-700 text-right px-4">{info}</div>}
      </div>
    </Tooltip>
  );
}
